import React, { useEffect, useRef, memo, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import './DistrictVillageLabel.css';

const DistrictVillageLabel = memo(({ 
  districtGeoData, 
  villageDataByDistrict: propVillageDataByDistrict,
  onDistrictClick,
  userState: propUserState,
  userDistrict: propUserDistrict,
  selectedState // Add selectedState prop to filter districts
}) => {
  const map = useMap();
  const labelsLayerRef = useRef(null);
  
  // Use passed props or fallback to localStorage
  const userState = propUserState;
  const userDistrict = propUserDistrict;
  const villageDataByDistrict = propVillageDataByDistrict || {};
  
  // Always show village counts for all users
  const showVillageCounts = true;

  // Helper to normalize names for matching
  const normalizeStateName = (name) => {
    if (!name) return '';
    return name.toUpperCase().trim().replace(/\s+/g, '');
  };

  useEffect(() => {
    // Clear existing labels
    if (labelsLayerRef.current) {
      map.removeLayer(labelsLayerRef.current);
      labelsLayerRef.current = null;
    }

    if (!districtGeoData || !villageDataByDistrict || Object.keys(villageDataByDistrict).length === 0) {
      return;
    }

    const labelsLayer = L.layerGroup();

    // Use neutral color for all badges (no water level classification)
    const badgeColor = '#2196F3'; // Blue - neutral color

    // Helper to normalize district names for matching
    const normalizeDistrictName = (name) => {
      if (!name) return '';
      return name.toUpperCase().trim().replace(/\s+/g, '');
    };

    // Process each district feature - ONLY for the selected state
    districtGeoData.features.forEach(feature => {
      const districtName = feature.properties.NAME_2 || feature.properties.dt_name;
      const featureStateName = feature.properties.NAME_1 || feature.properties.st_nm;
      
      if (!districtName || !featureStateName) return;

      // FILTER: Only show badges for districts in the selected state
      if (selectedState && normalizeStateName(featureStateName) !== normalizeStateName(selectedState)) {
        return; // Skip districts not in the selected state
      }

      const normalizedName = normalizeDistrictName(districtName);
      
      // Find matching village data
      const villageData = Object.keys(villageDataByDistrict).find(key => 
        normalizeDistrictName(key) === normalizedName
      );

      if (!villageData) return;

      const stats = villageDataByDistrict[villageData];
      const color = badgeColor;

      // Calculate center of district polygon
      let center;
      if (feature.geometry.type === 'Polygon') {
        const coords = feature.geometry.coordinates[0];
        const lats = coords.map(c => c[1]);
        const lngs = coords.map(c => c[0]);
        center = [
          (Math.min(...lats) + Math.max(...lats)) / 2,
          (Math.min(...lngs) + Math.max(...lngs)) / 2
        ];
      } else if (feature.geometry.type === 'MultiPolygon') {
        // Use first polygon's center
        const coords = feature.geometry.coordinates[0][0];
        const lats = coords.map(c => c[1]);
        const lngs = coords.map(c => c[0]);
        center = [
          (Math.min(...lats) + Math.max(...lats)) / 2,
          (Math.min(...lngs) + Math.max(...lngs)) / 2
        ];
      } else {
        return;
      }

      // Create simple badge with village count (smaller size) - hide count for state/district admins
      const icon = L.divIcon({
        className: 'district-village-label',
        html: `
          <div class="district-badge" style="background-color: ${color};">
            <div class="badge-count">${stats.count}</div>
            <div class="badge-label">villages</div>
          </div>
        `,
        iconSize: [45, 45],
        iconAnchor: [22.5, 22.5]
      });

      const marker = L.marker(center, { 
        icon,
        zIndexOffset: 1000 // Ensure labels appear above other elements
      });

      // Click handler
      marker.on('click', () => {
        if (onDistrictClick) {
          onDistrictClick(districtName, center);
        }
      });

      // Hover effect
      marker.on('mouseover', function() {
        const element = this.getElement();
        if (element) {
          element.classList.add('hovered');
        }
      });

      marker.on('mouseout', function() {
        const element = this.getElement();
        if (element) {
          element.classList.remove('hovered');
        }
      });

      labelsLayer.addLayer(marker);
    });

    // Add to map
    labelsLayer.addTo(map);
    labelsLayerRef.current = labelsLayer;

    // Cleanup
    return () => {
      if (labelsLayerRef.current) {
        map.removeLayer(labelsLayerRef.current);
      }
    };
  }, [districtGeoData, villageDataByDistrict, map, onDistrictClick, showVillageCounts, selectedState]);

  return null;
});

DistrictVillageLabel.displayName = 'DistrictVillageLabel';

export default DistrictVillageLabel;
